import React, { useMemo } from "react";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import {
  ADDRESS_MAP_OPTIONS,
  ADDRESS_MAP_ZOOM,
  GOOGLE_MAPS_BROWSER_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
} from "../utils/googleMaps";
import type { MappedAddress } from "../utils/address";

const MAP_CONTAINER_STYLE: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

/**
 * The map under a mapped address on the record page (#13870): the Facts tab
 * row and the Overview's Key facts rail. It is the editor's map, with the
 * editor's options: it pans, zooms and takes keyboard shortcuts in read mode.
 * The frame renders at once, before the Maps script loads, so the row never
 * shifts when the tiles arrive; a script that fails to load leaves no empty
 * box behind.
 */
const AddressMap: React.FC<{
  address: MappedAddress;
  height: number;
}> = ({ address, height }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_BROWSER_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  // `GoogleMap` recentres whenever `center` is a new object, and the row
  // around this map re-renders on every hover, so a centre rebuilt per render
  // snapped a reader's pan back the moment the pointer left the row. Keyed on
  // the numbers, the object changes only when the coordinates do.
  const { lat, lng } = address.coordinates;
  const position = useMemo<google.maps.LatLngLiteral>(
    () => ({ lat, lng }),
    [lat, lng]
  );

  if (loadError) return null;

  return (
    <div
      role="region"
      aria-label={`Map of ${address.formatted}`}
      className="w-full rounded-[6px] overflow-hidden"
      style={{
        height,
        marginTop: "var(--fp-gap-tight)",
        backgroundColor: "var(--fp-bg)",
      }}
    >
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={position}
          zoom={ADDRESS_MAP_ZOOM}
          options={ADDRESS_MAP_OPTIONS}
        >
          <MarkerF position={position} />
        </GoogleMap>
      ) : null}
    </div>
  );
};

export default AddressMap;
